import React, { useCallback, useEffect, useReducer, useState } from "react";
import { Button, Loading } from "@alm/flow-ds/components";

import SearchResults from "../Finder/SearchResults";
import { ViewDefinition } from "lib/api/types";
import { useTranslation } from "react-i18next";
import finderReducer, {
	addSearchResults,
	clearSelected,
	defaultState,
	setNewState,
	setSearchResults,
} from "../Finder/finderReducer";
import api from "lib/api";
import axios, { AxiosError } from "axios";
import DashboardLayout from "components/Layouts/DashboardLayout";
import ViewFinder from "./ViewFinder";
import PageHeader from "components/PageHeader";

import ErrorBoundary from "components/Errors/ErrorBoundary";
import { useDispatch } from "react-redux";
import { newForm, statusReady } from "data/form/formActions";
import { Workbench } from "components/Workbench";
import { createAlertAction } from "data/app/appActions";
import { createAlert } from "records/Alert";
import jsonpointer from "lib/jsonpointer/jsonpointer";
import _ from "lodash";
import { getErrorMessage } from "lib/api/errors";
import ViewForm from "components/Forms/Default/ViewForm";

function CustomViews() {
	const { t } = useTranslation();

	const dispatchForm = useDispatch();

	const [fid, setFid] = useState("");

	const [formAction, setFormAction] = useState<{
		type: "VIEW" | "EDIT" | "CREATE";
		index: number;
	} | null>(null);

	const [isDirty, setIsDirty] = useState(false);

	const [width, setWidth] = useState(window.innerWidth);

	const handleWindowSizeChange = () => setWidth(window.innerWidth);

	useEffect(() => {
		window.addEventListener("resize", handleWindowSizeChange);
		return () => {
			window.removeEventListener("resize", handleWindowSizeChange);
		};
	}, []);

	const [loadingPage, setLoadingPage] = useState(false);

	// Used to show a loader when making API requests
	const [cursorLoading, setCursorLoading] = useState(false);

	// Error message for 500s, things we can't recover from
	const [error, setError] = useState<undefined | string>();

	const [modalOpen, setModalOpen] = useState(true);

	const [viewDefinition, setViewDefinition] = useState<ViewDefinition | null>(
		null
	);

	// Setup our reducer to manage state
	const [
		{
			activeSearchFields,
			searchFields,
			query,
			resultsHasMore,
			results,
			selectedRecords,
			cursorId,
			querySummary,
		},
		dispatch,
	] = useReducer(finderReducer, defaultState({}));

	// When viewDefinition has loaded set the new finder reducer state
	useEffect(() => {
		if (viewDefinition !== null) {
			dispatch(
				setNewState({
					searchFields: viewDefinition.reference.search.view
						? viewDefinition.reference.search.view.elements?.reduce(
								(fields, e) =>
									Object.assign(fields, {
										[e.name]: e,
									}),
								{}
						  )
						: [],
					activeSearchFields: viewDefinition.reference.search.view
						? [
								viewDefinition.reference.search.view.elements[0]
									?.name,
						  ]
						: [],
				})
			);
		}
	}, [viewDefinition]);

	/**
	 * Search results pagination handler
	 */
	const handleLoadMore = useCallback(async () => {
		setLoadingPage(true);

		try {
			if (!cursorId) {
				throw new Error("Could not page, no cursorId found.");
			}
			const resp = await api.cursors.nextPage(cursorId);
			dispatch(addSearchResults(resp));
		} catch (error) {
			console.error(error);
			if (error && axios.isAxiosError(error)) setError(error.message);
		} finally {
			setLoadingPage(false);
		}
	}, [cursorId]);

	/**
	 * Handle reload results
	 */
	const reloadResults = useCallback(async () => {
		if (viewDefinition !== null) {
			// Show the loader
			setCursorLoading(true);

			// Reset isDirty
			setIsDirty(false);

			// Clear any selected records from previous searches
			dispatch(clearSelected());

			// Create our query payload
			const payload = _.cloneDeep(
				viewDefinition.reference.search.payload
			);
			payload.filter.AND = [...payload.filter.AND, ...query];
			payload.select = viewDefinition.reference.search.columns.map(c =>
				jsonpointer.pointerToId(c.id)
			);

			try {
				const resp = await api.cursors.create(payload);
				dispatch(setSearchResults(resp));
			} catch (err) {
				const e = err as AxiosError;
				setError(
					getErrorMessage(e) ||
						t("elements.finder.genericSearchErrorMsg")
				);
			} finally {
				setCursorLoading(false);
				setModalOpen(false);
			}
		}
	}, [
		viewDefinition,
		setCursorLoading,
		dispatch,
		setModalOpen,
		query,
		setError,
		t,
	]);

	// Dispatch error alert
	const dispatchError = useCallback(
		(error, id) => {
			dispatchForm(
				createAlertAction(
					createAlert({
						title: t(`alerts.${error}.title`),
						description: t(`alerts.${error}.description`, {
							formDefValue: id,
						}),
						status: "error",
					})
				)
			);
		},
		[dispatchForm, t]
	);

	// Load the form, if create action is set then create new record
	const loadForm = useCallback(
		async (formAction: "CREATE" | "EDIT" | "VIEW", id?: string) => {
			if (viewDefinition !== null) {
				const definition =
					formAction === "CREATE"
						? viewDefinition.reference.createLink
						: formAction === "VIEW"
						? viewDefinition.reference.viewLink
						: viewDefinition.reference.updateLink;

				const recordId = id;

				if (definition) {
					try {
						const instance = await api.forms.create(
							definition,
							formAction !== "CREATE" ? recordId : null
						);
						dispatchForm(newForm(instance.instanceId, instance));
						dispatchForm(statusReady(instance.instanceId));
						setFid(instance.instanceId);
					} catch (error) {
						dispatchError("formUnfound", definition);
					}
				}
			}
		},
		[dispatchForm, dispatchError, viewDefinition]
	);

	// When fid is updated reload results list if dirty
	useEffect(() => {
		if (isDirty) reloadResults();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fid]);

	// Run loadForm when formAction is updated
	useEffect(() => {
		if (formAction !== null && results && results[formAction.index]) {
			loadForm(formAction.type, results[formAction.index].id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formAction]);

	return (
		<DashboardLayout isAutoScroll={true}>
			<ViewFinder
				title={t(`main.views.headings.selectSource`)}
				finder={{
					activeSearchFields,
					searchFields,
					query,
					resultsHasMore,
					results,
					selectedRecords,
					cursorId,
					querySummary,
				}}
				dispatch={dispatch}
				viewDefinition={viewDefinition}
				setViewDefinition={setViewDefinition}
				setCursorLoading={setCursorLoading}
				setError={setError}
				modalOpen={modalOpen}
				setModalOpen={setModalOpen}
			/>
			<Workbench>
				{error ? (
					<>
						<div
							className="alert show block-alert block-alert--error"
							role="alert">
							<div className="alert__message">
								<div className="alert__desc">
									<p className="mb-0">{error}</p>
								</div>
							</div>
						</div>
						<div className="finder__toggle-form-visibility ms-auto me-md-auto">
							<Button
								className="btn-link"
								label={t(
									"elements.finder.modify-criteria-btn-label"
								)}
								onClick={() => setModalOpen(true)}
							/>
						</div>
					</>
				) : (
					<div className="container custom-views">
						{cursorLoading ? (
							<Loading className="mt-4 elem__loader--md" />
						) : (
							results !== undefined &&
							viewDefinition !== null && (
								<>
									<div className="list-group-fixed-header">
										<PageHeader
											title={viewDefinition.viewLabel}
											className="page-header__views">
											{viewDefinition.reference
												.createLink && (
												<div>
													<Button
														className="btn-primary tasks__start-btn"
														label={t(
															"main.labels.create"
														)}
														onClick={() => {
															setFormAction({
																type: "CREATE",
																index: 0,
															});
															loadForm("CREATE");
														}}
													/>
												</div>
											)}
										</PageHeader>
										<div className="finder__search-toolbar">
											<div className="finder__search-summary d-none d-md-flex">
												<span className="finder__search-summary-title me-2">
													{t(
														"elements.finder.search-criteria-title"
													)}
												</span>
												{activeSearchFields.map(
													(f, i) => (
														<span
															className="finder__search-summary-field me-2"
															key={`summ-${i}`}>
															<span className="finder__search-summary-field-name me-1">
																{
																	searchFields[
																		f
																	]?.label
																}
																:
															</span>
															<span className="finder__search-summary-field-value">
																<small>
																	{
																		querySummary[
																			i
																		]
																	}
																</small>
															</span>
														</span>
													)
												)}
											</div>
											<div className="finder__toggle-form-visibility ms-auto ms-md-2">
												<Button
													className="btn-link"
													label={t(
														"elements.finder.modify-criteria-btn-label"
													)}
													onClick={() =>
														setModalOpen(true)
													}
												/>
											</div>
										</div>
									</div>
									<SearchResults
										hasMore={resultsHasMore}
										results={results}
										onLoadMore={handleLoadMore}
										columns={
											viewDefinition.reference.search
												.columns
										}
										selected={selectedRecords}
										loadingPage={loadingPage}
										addSelected={() =>
											console.log("Add selected")
										}
										hideSelectBoxes={true}
										isMobile={width < 992 ? true : false}
										setFormAction={setFormAction}
										showEdit={
											viewDefinition.reference
												.updateLink !== undefined
										}
										showView={
											viewDefinition.reference
												.viewLink !== undefined
										}
									/>
								</>
							)
						)}
					</div>
				)}

				{fid ? (
					<ErrorBoundary>
						{fid ? (
							<ViewForm
								fid={fid}
								workbenchIndex={0}
								closeForm={() => setFid("")}
								formAction={
									formAction !== null
										? formAction.type
										: "CREATE"
								}
								setIsDirty={setIsDirty}
								breadcrumb={
									viewDefinition
										? [
												{
													name: viewDefinition.viewLabel,
												},
										  ]
										: []
								}
							/>
						) : null}
					</ErrorBoundary>
				) : (
					<></>
				)}
			</Workbench>
		</DashboardLayout>
	);
}

export default CustomViews;
